/**
 * SystemDox notification adapter.
 *
 * Bridges @puglieseweb/shared-react-notifications to the durable notifications
 * API (`services/notificationsApi`) plus the AI API's existing job poller, so
 * document-generation results survive a reload and populate the /notifications
 * history page.
 *
 * Shape note: the package's `listNotifications()` returns `Job[]`, and the
 * provider wraps each one as `NotificationItem { id: 'job-<id>', job, type }`.
 * That wrapper carries no `actionUrl`, so a stored notification's link is
 * ferried through `job.meta.actionUrl` — read it back with
 * `notificationActionUrl()` rather than `notification.actionUrl` alone.
 */

import type { Job, NotificationAdapter, NotificationItem } from '@puglieseweb/shared-react-notifications';
import { notificationsApi } from '@/services/notificationsApi';
import type { NotificationRecord } from '@/services/notificationsApi';
import { createLogger } from '@/lib/logger';

const log = createLogger('notificationAdapter');

/**
 * Stored notification -> the shared Job shape the panel renders.
 *
 * Mirrors `_notification_to_job` in backend/functions/notifications_api/app.py,
 * which does the same mapping for the single-job poll — keep the two in step.
 */
function toJob(record: NotificationRecord): Job {
  const failed = record.type === 'failed';
  const iso = new Date(record.timestamp || Date.now()).toISOString();
  return {
    id: record.id,
    status: failed ? 'failed' : 'completed',
    label: record.title,
    ...(record.description ? { sublabel: failed ? undefined : record.description } : {}),
    ...(failed && record.description ? { error: record.description } : {}),
    ...(!failed && record.description ? { statusMessage: record.description } : {}),
    createdAt: iso,
    updatedAt: iso,
    meta: {
      ...(record.actionUrl ? { actionUrl: record.actionUrl } : {}),
      notificationType: record.type,
    },
  };
}

/**
 * How many notifications the bell reads per poll.
 *
 * The panel shows only the latest handful, so pulling the server's full page
 * on every tick is waste. Anything further back is the /notifications page's
 * job, and that page paginates its own way there.
 */
export const BELL_PAGE_SIZE = 10;

/**
 * Stored notification -> the NotificationItem the shared components render.
 *
 * Exported for surfaces that own their own list (the paginated /notifications
 * page) rather than reading the provider's. It deliberately reproduces the
 * provider's wrapping EXACTLY, because the same notification is rendered from
 * both sources and any divergence shows up as one item looking different in
 * the bell and on the page:
 *
 *  - the `job-` id prefix is LOAD-BEARING, not cosmetic. The provider's
 *    dismisser only calls the adapter for ids starting with it; anything else
 *    is treated as ephemeral and dropped locally with NO server call, so a raw
 *    `job_<hex>` id would make dismiss appear to work and never persist.
 *  - `type` comes from the mapped job status, not from `record.type`, which is
 *    what the provider does. That flattens `info`/`warning` to `completed`;
 *    matching the provider matters more than the lost nuance.
 */
export function recordToNotificationItem(record: NotificationRecord): NotificationItem {
  const job = toJob(record);
  return {
    id: `job-${job.id}`,
    job,
    type: job.status === 'completed' ? 'completed' : 'failed',
    timestamp: job.updatedAt ? new Date(job.updatedAt).getTime() : Date.now(),
  };
}

/**
 * The route a notification links to.
 *
 * Ephemeral notifications carry `actionUrl` directly; durable ones arrive
 * wrapped in a Job, whose meta holds it. Click handlers must consult both.
 */
export function notificationActionUrl(notification: NotificationItem): string | undefined {
  if (notification.actionUrl) return notification.actionUrl;
  const fromJob = notification.job?.meta?.actionUrl;
  return typeof fromJob === 'string' ? fromJob : undefined;
}

export const systemdoxNotificationAdapter: NotificationAdapter = {
  // The bell renders on every page, so a failed list read must not surface as
  // an unhandled rejection — log it and show nothing.
  async listPendingJobs(): Promise<Job[]> {
    try {
      return (await notificationsApi.listPending()) as Job[];
    } catch (err) {
      log.error('Failed to list pending jobs', err as Error);
      return [];
    }
  },

  // Only the newest page: this backs the bell, which shows a handful. The
  // adapter interface deliberately gains NO cursor — pagination belongs to the
  // page that owns the data, not to the provider — so the page token this
  // drops on the floor is the right thing to drop.
  async listNotifications(): Promise<Job[]> {
    try {
      const { notifications } = await notificationsApi.list({ limit: BELL_PAGE_SIZE });
      return notifications.map(toJob);
    } catch (err) {
      log.error('Failed to list notifications', err as Error);
      return [];
    }
  },

  /**
   * The provider REPLACES the polled job with whatever this returns, and builds
   * the completion notification from the same object — so anything not returned
   * here is destroyed on the first tick. Hence the whole Job comes from the
   * server: synthesising one locally relabelled every job 'Generating document',
   * dropped its sublabel, churned its timestamps on every tick, and left
   * `meta.actionUrl` undefined, which made the finished row unclickable.
   */
  async getJobStatus(jobId: string): Promise<Job> {
    return (await notificationsApi.getJob(jobId)) as Job;
  },

  async dismissJob(jobId: string): Promise<void> {
    await notificationsApi.dismiss(jobId);
  },

  async dismissAllJobs(): Promise<void> {
    await notificationsApi.dismissAll();
  },

  /**
   * Not supported, and deliberately loud about it.
   *
   * A retry means re-running document assembly with the original captured
   * elements, which live client-side in IndexedDB and are never sent to the
   * server — there is nothing to re-run from. The panel renders its Retry
   * button for any failed notification carrying a job and gives us no way to
   * suppress it, so the only choice is what happens on click. Resolving is
   * worse than rejecting: the provider would then re-add the job as a
   * permanent "Retrying…" spinner and announce "The job has been re-queued for
   * processing", which is simply untrue. Rejecting surfaces this message
   * instead, which tells the user exactly what to do.
   */
  async retryJob(): Promise<void> {
    throw new Error('This generation can only be re-run from Add, with the original captured items.');
  },
};
